import {
  Button,
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  InplaceEdit,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { FileListUploader } from 'components/Files/components/FileList/FileListUploader';
import { BpFile } from 'components/Files/hooks/useFiles';
import { TipTapEditor } from 'components/TipTap/TipTapEditor';
import dayjs from 'dayjs';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useCreateSelectOptions } from 'hooks/useCreateSelectOptions';
import { useRequireParams } from 'hooks/useRequireParams';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { isAfter, isBefore, isSame } from 'utils/dateCalculations';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import * as Yup from 'yup';
import { connectDay, fileEntriesConnect } from '../../../utils/connectLib';
import { BpCard } from '../../BpCard/BpCard';
import { CreateAssignmentMutation, UpdateAssignmentMutation, useAssignments } from '../hooks/useAssignments';
import styles from './AssignmentForm.module.scss';

const AssignmentFormPageQuery = graphql(`
  query AssignmentFormPageData($courseUuid: ID!) {
    teachingUnits(where: { group: { uuid: $courseUuid } }) {
      __typename
      uuid
      title
    }
  }
`);

type AssignmentFormType = {
  title: string;
  description: string | null;
  visibleFrom: Date;
  dueDate: Date;
  teachingUnitUuid: string | null;
  allowSubmissionAfterDueDate: boolean;
  fileEntries: BpFile[];
};

type AssignmentFormProps = {
  teachingUnitUuid?: string;
  assignmentUuid: string | null;
  isModal?: boolean;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export function AssignmentForm({ teachingUnitUuid, assignmentUuid, isModal, onLoad, onClose }: AssignmentFormProps) {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const [, bpCreateAssignment] = useMutation(CreateAssignmentMutation);
  const [, updateAssignment] = useMutation(UpdateAssignmentMutation);

  const { assignments } = useAssignments({ courseUuid, assignmentUuid: assignmentUuid ?? undefined });

  const [{ data }] = useQuery({
    query: AssignmentFormPageQuery,
    variables: { courseUuid, teachingUnitUuid },
    pause: !courseUuid,
  });

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  const teachingUnits = data?.teachingUnits;

  const teachingUnitOptions = useCreateSelectOptions(teachingUnits, 'uuid', 'title');

  const today: Date = useMemo(() => new Date(), []);

  const initialValues: AssignmentFormType = {
    title: assignment?.title ?? t('assignments.newAssignment'),
    description: assignment?.material?.text ?? null,
    visibleFrom: assignment?.visibleFrom ?? today,
    dueDate: assignment?.dueDate ?? today,
    teachingUnitUuid: assignment
      ? assignment.teachingUnit.uuid
      : teachingUnits
        ? teachingUnits.length === 1
          ? teachingUnits[0].uuid
          : (teachingUnitUuid ?? null)
        : null,
    allowSubmissionAfterDueDate: assignment?.allowSubmissionAfterDueDate ?? false,
    fileEntries: assignment?.material?.files ?? [],
  };

  const schema = Yup.object().shape({
    title: Yup.string().required(t('assignments.validation.titleRequired')).min(3, t('validation.textTooShort')),
    teachingUnitUuid: Yup.string().required(t('assignments.validation.teachingUnitRequired')),
  });

  async function handleSubmit(values: AssignmentFormType, helpers: FormikHelpers<AssignmentFormType>) {
    onLoad(true);

    const dueDate = dayjs(values.dueDate).toISOString();
    const visibleFromDate = dayjs(values.visibleFrom).toISOString();

    if (assignmentUuid) {
      const { error } = await updateAssignment({
        where: { uuid: assignmentUuid },
        update: {
          title: values.title,
          allowSubmissionAfterDueDate: values.allowSubmissionAfterDueDate,
          dueDate: {
            disconnect: {},
            ...connectDay(dueDate),
          },
          visibleFrom: {
            disconnect: {},
            ...connectDay(visibleFromDate),
          },
          holder: {
            TeachingUnit: {
              connect: {
                where: { node: { uuid: values.teachingUnitUuid } },
              },
              disconnect: {},
            },
          },
          material: {
            update: {
              node: {
                text: values.description,
                fileEntries: [fileEntriesConnect(values.fileEntries)],
              },
            },
          },
        },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await bpCreateAssignment({
        input: {
          title: values.title,
          course: { connect: { where: { node: { uuid: courseUuid } } } },
          dueDate: {
            ...connectDay(dueDate),
          },
          visibleFrom: { ...connectDay(visibleFromDate) },
          material: {
            create: {
              node: {
                text: values.description,
                textMediaType: 'text/plain',
                fileEntries: fileEntriesConnect(values.fileEntries),
              },
            },
          },
          allowSubmissionAfterDueDate: values.allowSubmissionAfterDueDate,
          holder: {
            TeachingUnit: {
              connect: {
                where: {
                  node: {
                    uuid: values.teachingUnitUuid,
                  },
                },
              },
            },
          },
        },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  }

  return (
    <Formik<AssignmentFormType> onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema}>
      {({ values, dirty, setFieldValue, setFieldTouched, errors }) => {
        return (
          <Form className={styles['assignment-form']}>
            <Grid>
              <GridRow>
                <GridColumn width={8}>
                  <GridRow className={styles.title}>
                    <InplaceEdit
                      dense
                      name='title'
                      size='xxl'
                      onBlur={async (value) => {
                        await setFieldValue('title', value);
                        await setFieldTouched('title', true);
                      }}
                      onChange={async (value) => {
                        await setFieldValue('title', value);
                        await setFieldTouched('title', true);
                      }}
                      error={errors.title}
                      defaultValue={values.title}
                      placeholder={t('common.addTitle')}
                      backgroundColor={isModal ? 'white' : 'background'}
                    />
                  </GridRow>
                  <GridRow spacingTop='none' spacingBottom='none'>
                    <BpCard isEmbedded={isModal} header={{ headline: t('assignments.task') }} preventCollapsible>
                      <TipTapEditor
                        defaultValue={values.description}
                        onChange={async (value) => {
                          await setFieldValue('description', value);
                          await setFieldTouched('description', true);
                        }}
                        name='description'
                        isDense
                      />
                    </BpCard>
                  </GridRow>
                  <GridRow>
                    <BpCard
                      isEmbedded={isModal}
                      header={{ headline: t('files.attachment', { count: 2 }) }}
                      preventCollapsible
                    >
                      <FileListUploader
                        files={values.fileEntries}
                        onFileUpload={async (file) => {
                          await setFieldValue('fileEntries', [...values.fileEntries, file]);
                          await setFieldTouched('fileEntries', true);
                        }}
                        onFileRename={async (file) => {
                          await setFieldValue(
                            'fileEntries',
                            values.fileEntries.map((f) => (f.uuid === file.uuid ? file : f)),
                          );
                        }}
                        onFilesDelete={async (uuids) => {
                          await setFieldValue(
                            'fileEntries',
                            values.fileEntries.filter((f) => !uuids.includes(f.uuid)),
                          );
                        }}
                      />
                    </BpCard>
                  </GridRow>
                </GridColumn>
                <GridColumn width={4}>
                  <GridRow className={styles.actions}>
                    <Button hierarchy='tertiary' disabled>
                      {t('common.archive')}
                    </Button>
                    <Button hierarchy='secondary' onClick={() => onClose()}>
                      {t('common.cancel')}
                    </Button>
                    <Button hierarchy='primary' type='submit' disabled={!dirty || Object.keys(errors).length > 0}>
                      {t('common.save')}
                    </Button>
                  </GridRow>
                  <GridRow spacingTop='none'>
                    <BpCard
                      isEmbedded={isModal}
                      header={{ headline: t('teachingUnits.titleSingular') }}
                      preventCollapsible
                    >
                      <Select
                        dense
                        placeholder={t('common.chooseType', {
                          type: t('teachingUnits.titleSingular'),
                        })}
                        name='teachingUnitSelect'
                        defaultValue={teachingUnitOptions.find((v) => v.value === values.teachingUnitUuid)}
                        options={teachingUnitOptions}
                        onChange={async (option) => {
                          const value = (option as SingleValue<SelectOptionType>)?.value;
                          await setFieldValue('teachingUnitUuid', value);
                          await setFieldTouched('teachingUnitUuid', true);
                        }}
                        error={errors.teachingUnitUuid}
                        menuPosition='fixed'
                        readonly={!!assignment?.teachingUnit.uuid}
                      />
                    </BpCard>
                  </GridRow>
                  <GridRow spacingTop='none'>
                    <BpCard isEmbedded={isModal} header={{ headline: t('assignments.dueDate') }} preventCollapsible>
                      <DatePicker
                        name='visibleFrom'
                        dateFormat='dd.MM.yyyy'
                        label={t('common.visibleFrom')}
                        onChange={async (e) => {
                          const date = e ?? today;
                          if (isAfter(date, values.dueDate)) {
                            await setFieldValue('dueDate', date);
                            await setFieldTouched('dueDate');
                          }
                          await setFieldValue('visibleFrom', date);
                          await setFieldTouched('visibleFrom');
                        }}
                        value={values.visibleFrom}
                        minDate={today}
                        holidays={
                          !isSame(values.visibleFrom, values.dueDate)
                            ? [{ date: values.dueDate, holidayName: t('assignments.dueAt') }]
                            : undefined
                        }
                        holidayStyling='grey'
                      />
                      <DatePicker
                        className='mt-2'
                        name='dueAt'
                        dateFormat='dd.MM.yyyy'
                        label={t('assignments.dueAt')}
                        onChange={async (e) => {
                          const date = e ?? today;
                          if (isBefore(date, values.visibleFrom)) {
                            await setFieldValue('visibleFrom', date);
                            await setFieldTouched('visibleFrom');
                          }
                          await setFieldValue('dueDate', date);
                          await setFieldTouched('dueDate');
                        }}
                        value={values.dueDate}
                        minDate={today}
                        holidays={
                          !isSame(values.dueDate, values.visibleFrom)
                            ? [{ date: values.visibleFrom, holidayName: t('common.visibleFrom') }]
                            : undefined
                        }
                        holidayStyling='grey'
                      />
                      <Checkbox
                        className='mt-2'
                        label={t('submissions.allowAfterDue')}
                        name='allowSubmissionAfterDueDate'
                        checked={values.allowSubmissionAfterDueDate ?? false}
                        onChange={async (value) => {
                          setFieldValue('allowSubmissionAfterDueDate', value.target.checked);
                          setFieldTouched('allowSubmissionAfterDueDate');
                        }}
                      />
                    </BpCard>
                  </GridRow>
                </GridColumn>
              </GridRow>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
